import { CheckCircle, Clock, LogOut, XCircle } from 'lucide-react';
import type { ComponentType } from 'react';
import type { AttendanceStatus } from '../../types/attendance';

/**
 * Attendance states in the teacher's own words.
 *
 * The database keeps its original status values (`not_arrived` / `checked_in` /
 * `picked_up` / `absent`), but everything a teacher reads is phrased as check-in and
 * check-out.
 */
export const ROSTER_STATUS_LABEL: Record<AttendanceStatus, string> = {
  checked_in: 'Checked in',
  picked_up: 'Checked out',
  absent: 'Absent',
  not_arrived: 'Not checked in',
};

const STATUS_STYLE: Record<AttendanceStatus, { icon: ComponentType<{ className?: string }>; chip: string }> = {
  checked_in: { icon: CheckCircle, chip: 'bg-green-100 text-green-800' },
  picked_up: { icon: LogOut, chip: 'bg-blue-100 text-blue-800' },
  absent: { icon: XCircle, chip: 'bg-red-100 text-red-700' },
  not_arrived: { icon: Clock, chip: 'bg-gray-100 text-gray-500' },
};

interface Props {
  status: AttendanceStatus;
  /**
   * How the four states are named here.
   *
   * Defaults to the roster's wording. History overrides one of them: on a day already past,
   * `not_arrived` means nobody recorded anything either way — an unknown, not a state the
   * child is still in — so it reads "No record" there rather than "Not checked in".
   */
  labels?: Record<AttendanceStatus, string>;
}

/**
 * One attendance state as a chip, shared by the roster and the archive.
 *
 * The same four colours and glyphs in both places, deliberately: a teacher reading last
 * Tuesday should not have to learn a second vocabulary for what they recorded on the day.
 */
export function AttendanceStatusBadge({ status, labels = ROSTER_STATUS_LABEL }: Props) {
  const { icon: Icon, chip } = STATUS_STYLE[status];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${chip}`}
    >
      <Icon className="h-3 w-3" />
      {labels[status]}
    </span>
  );
}
